import React, { useState, useEffect, useCallback } from 'react'
import { COUNTDOWN_SECONDS } from '@/constant'
import { isEmpty } from '@/utils'
import { Button, TextField } from '@mui/material'
import { LoadingButton } from '@/components/Button'
import { BasicCard, Footer, ValidationButton, FlexRow, ErrorText } from './components'
import Modal from '@/components/Modal'
import Text from '@/components/Text'
import Form, { FormItem } from '@/components/Form'
import { useMutation } from '@tanstack/react-query'
import { getBindMobileCode, bindMobile } from '@/api'
import { Message } from '@cx/ui'

export interface BindMobileProps {
  open: boolean
  onClose: () => void
}

export interface FormValueProps {
  phone_number: string
  code: string
}

const BindMobile: React.FC<BindMobileProps> = (props) => {
  const { open, onClose } = props
  const [formValue, setFormValue] = useState<FormValueProps>({
    phone_number: '',
    code: '',
  })
  const [hasError, setHasError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [validating, setValidation] = useState<boolean>(false)

  const bindMobileMutation = useMutation(bindMobile, {
    onError: (error) => {
      setValidation(false)
      setLoading(false)
    },
    onSuccess: () => {
      Message.success('绑定成功')
      setValidation(false)
      setLoading(false)
      onClose()
    },
    onMutate: () => {
      setValidation(true)
      setLoading(true)
    },
  })

  const handleFormChange = useCallback((formValue: FormValueProps) => {
    setFormValue(formValue)
  }, [])

  const handleValidations = useCallback((hasErrors: boolean) => {
    setHasError(hasErrors)
  }, [])

  const handleBind = useCallback(() => {
    bindMobileMutation.mutate(formValue)
  }, [bindMobileMutation, formValue])

  return (
    <Modal width={500} open={open} onClose={onClose}>
      <BasicCard>
        <Text>绑定手机号</Text>
        <Form
          initialValue={formValue}
          // @ts-ignore
          onFormValueChange={handleFormChange}
          // @ts-ignore
          onValidate={handleValidations}
          validating={validating}
        >
          <FormItem
            label='phone_number'
            text={'手机号码'}
            rules={{
              errorMessage: '号码不能为空',
              validator: (value: string) => value?.length > 0,
            }}
          >
            <TextField placeholder={'请输入手机号码'} fullWidth={true} type='text' />
          </FormItem>
          <FormItem
            label='code'
            text={'验证码'}
            rules={{
              errorMessage: '验证码不能为空',
              validator: (value: string) => value?.length > 0,
            }}
          >
            <ValidationCode phoneNumber={formValue?.phone_number} />
          </FormItem>
        </Form>
        <Footer>
          <Button variant='outlined' onClick={onClose}>
            取消
          </Button>
          <LoadingButton loading={loading} disabled={hasError} onClick={handleBind}>
            确认绑定
          </LoadingButton>
        </Footer>
      </BasicCard>
    </Modal>
  )
}

export const ValidationCode = ({
  phoneNumber,
  value,
  label,
  onChange,
}: {
  phoneNumber?: string
  label?: string
  value?: unknown
  onChange?: () => void
}) => {
  const [second, setSecond] = useState<number>(COUNTDOWN_SECONDS)
  const [timing, setTiming] = useState(false)
  const [sended, setSended] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const mailCodeMutation = useMutation(getBindMobileCode, {
    onError: () => {
      setTiming(false)
      setSended(false)
    },
    onSuccess: () => {
      Message.success('验证码已发送至手机')
      setTiming(true)
    },
    onMutate: () => {
      setErrorMessage('')
      setSended(true)
    },
  })

  const handleGetCode = useCallback(() => {
    // @ts-ignore
    mailCodeMutation.mutate({ phone_number: phoneNumber })
  }, [mailCodeMutation, phoneNumber])

  useEffect(() => {
    let timer: NodeJS.Timeout
    function countdown() {
      setSecond((preSecond) => {
        if (preSecond <= 1) {
          setTiming(false)
          setSended(true)
          return COUNTDOWN_SECONDS
        } else {
          timer = setTimeout(countdown, 1000)
          return preSecond - 1
        }
      })
    }
    if (timing) {
      timer = setTimeout(countdown, 1000)
    }
    return () => clearTimeout(timer)
  }, [timing])

  return (
    <>
      <FlexRow sx={{ justifyContent: 'space-between' }}>
        <TextField label={label} placeholder={'请输验证码'} type='text' value={value} onChange={onChange} />
        <ValidationButton disabled={sended || isEmpty(phoneNumber)} onClick={handleGetCode}>
          {sended ? `再次发送：${second}s` : '发送验证码'}
        </ValidationButton>
      </FlexRow>
      <ErrorText>{errorMessage}</ErrorText>
    </>
  )
}

export default BindMobile
